<template>
  <BaseTable
    :isLoading="isLoading"
    :tableData="data"
    :totalItemsCount="totalItemsCount"
    :perPage="perPage"
    :currentPage="currentPage"
    :showSearch="true"
    searchPlaceholder="Search by value"
    :showCreateButton="isGlobalAdmin"
    createLabel="Add BlackList"
    @create="$emit('openModal')"
    @search="onSearch"
    @clear="onClear"
    @paginate="paginate"
  >
    <template #filters>
      <div class="blacklist-filter">
        <div
          v-if="showBlackListFilters"
          class="blacklist-filter__backdrop"
          @click="showBlackListFilters = false"
        ></div>
        <Button
          :icon="hasActiveBlackListFilters ? 'pi pi-filter-fill' : 'pi pi-filter'"
          :class="[
            'blacklist-filter__btn',
            { 'blacklist-filter__btn--active': hasActiveBlackListFilters },
          ]"
          v-tooltip.bottom="'Filters'"
          @click.stop="showBlackListFilters = !showBlackListFilters"
        />
        <div v-if="showBlackListFilters" class="blacklist-filter__dropdown" @click.stop>
          <div class="blacklist-filter__field">
            <label class="blacklist-filter__label">Category</label>
            <Dropdown
              v-model="blackListFilters.category"
              :options="blCategoryOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="All categories"
              showClear
              class="blacklist-filter__select"
            />
          </div>
          <div class="blacklist-filter__field">
            <label class="blacklist-filter__label">Type</label>
            <Dropdown
              v-model="blackListFilters.type"
              :options="blTypeOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="All types"
              showClear
              class="blacklist-filter__select"
            />
          </div>
          <div class="blacklist-filter__field">
            <label class="blacklist-filter__label">Status</label>
            <Dropdown
              v-model="blackListFilters.status"
              :options="blStatusOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="All statuses"
              showClear
              class="blacklist-filter__select"
            />
          </div>
          <div class="blacklist-filter__filter-actions">
            <b-button
              variant="outline-secondary"
              size="sm"
              @click="clearBlackListFilters"
            >Clear</b-button>
            <b-button
              class="blacklist-filter__apply-btn"
              size="sm"
              @click="applyBlackListFilters"
            >Apply</b-button>
          </div>
        </div>
      </div>
    </template>
    <template #columns>
      <Column field="id" header="Id" :styles="{ 'min-width': '10rem' }">
        <template #body="{ data }">
          <p>{{ data.id }}</p>
        </template>
      </Column>
      <Column field="type" header="Type" :styles="{ 'min-width': '8rem' }">
        <template #body="{ data }">
          <p>{{ data.type }}</p>
        </template>
      </Column>
      <Column field="value" header="Value" :styles="{ 'min-width': '12rem' }">
        <template #body="{ data }">
          <p>{{ data.value }}</p>
        </template>
      </Column>
      <Column field="category" header="Category" :styles="{ 'min-width': '10rem' }">
        <template #body="{ data }">
          <p>{{ data.category }}</p>
        </template>
      </Column>
      <Column field="status" header="Status" :styles="{ 'min-width': '8rem' }">
        <template #body="{ data }">
          <p>{{ data.status }}</p>
        </template>
      </Column>
      <Column field="queue_send" header="Queue Send" :styles="{ 'min-width': '8rem' }">
        <template #body="{ data }">
          <p>{{ data.queue_send }}</p>
        </template>
      </Column>
      <Column field="created_by" header="Created By" :styles="{ 'min-width': '10rem' }">
        <template #body="{ data }">
          <p>{{ data.created_by }}</p>
        </template>
      </Column>
      <Column field="created_at" header="Created At" :styles="{ 'min-width': '10rem' }">
        <template #body="{ data }">
          <p>{{ formatDate(data.created_at) }}</p>
        </template>
      </Column>
      <Column field="last_modified" header="Last Modified" :styles="{ 'min-width': '10rem' }">
        <template #body="{ data }">
          <p>{{ formatDate(data.last_modified) }}</p>
        </template>
      </Column>
    </template>

    <!-- Read only for non global admins: writes are global admin only -->
    <template v-if="isGlobalAdmin" #actions-column>
      <Column header="Actions">
        <template #body="{ data }">
          <div class="data-table__actions">
            <span @click="$emit('openModal', data)">
              <i class="pi pi-pencil"></i>
            </span>
          </div>
        </template>
      </Column>
    </template>
  </BaseTable>
</template>

<script>
import { mapGetters } from "vuex";
import { successMessage, deleteMessage } from "@/utils/alertMessages";
import BaseTable from "./BaseTable.vue";

export default {
  name: "BlackListTable",
  components: { BaseTable },
  emits: ["openModal"],
  data() {
    return {
      isLoading: false,
      data: [],
      loggedUser: null,
      currentCompanyDepartment: null,
      totalItemsCount: 0,
      currentPage: 1,
      perPage: 15,
      searchField: "",
      showBlackListFilters: false,
      blackListFilters: {
        category: null,
        type: null,
        status: null,
      },
      blCategoryOptions: [
        { label: "GLT", value: "glt" },
        { label: "ACCU", value: "accu" },
        { label: "TAI/EFreight", value: "tai/efreight" },
        { label: "Agents", value: "agents" },
        { label: "Special Customers", value: "special customers" },
        { label: "Blacklist LTL", value: "blacklist ltl" },
        { label: "Manual Handling", value: "manual handling" },
        { label: "Carriers", value: "carriers" },
        { label: "Identified Shippers", value: "identified shippers" },
      ],
      blTypeOptions: [
        { label: "Email", value: "email" },
        { label: "Domain", value: "domain" },
      ],
      blStatusOptions: [
        { label: "Active", value: "active" },
        { label: "Inactive", value: "inactive" },
      ],
    };
  },
  computed: {
    ...mapGetters("blackList", ["showAllBlackList"]),
    ...mapGetters("user", ["showCurrentCompanyDepartment"]),
    isGlobalAdmin() {
      return this.loggedUser?.role === "global_admin";
    },
    hasActiveBlackListFilters() {
      const { category, type, status } = this.blackListFilters;
      return !!(category || type || status);
    },
  },
  watch: {
    showAllBlackList() {
      if (this.showAllBlackList) {
        this.data = this.showAllBlackList?.data;
        this.totalItemsCount = this.showAllBlackList?.count;
      }
    },
    async showCurrentCompanyDepartment() {
      if (this.showCurrentCompanyDepartment) {
        this.currentCompanyDepartment = this.showCurrentCompanyDepartment;
        this.currentPage = 1;
        this.searchField = "";
        this.blackListFilters = { category: null, type: null, status: null };
        await this.getAllBlackList();
      }
    },
  },
  async created() {
    this.loggedUser = JSON.parse(localStorage.getItem("user-info"));
    this.currentCompanyDepartment = this.showCurrentCompanyDepartment;
    await this.getAllBlackList();
  },
  methods: {
    async getAllBlackList(payload) {
      this.isLoading = true;
      const params = {
        limit: 15,
        skip: (this.currentPage - 1) * 15,
      };
      if (payload) {
        if (payload.type) params.type = payload.type;
        if (payload.value) params.value = payload.value;
        if (payload.status) params.status = payload.status;
        if (payload.category) params.category = payload.category;
      }
      try {
        await this.$store.dispatch("blackList/getAllBlackList", params);
        this.data = this.showAllBlackList?.data;
        this.totalItemsCount = this.showAllBlackList?.count;
      } finally {
        this.isLoading = false;
      }
    },
    async applyBlackListFilters() {
      this.showBlackListFilters = false;
      this.currentPage = 1;
      const payload = { ...this.blackListFilters };
      if (this.searchField) payload.value = this.searchField;
      await this.getAllBlackList(Object.values(payload).some(Boolean) ? payload : undefined);
    },
    clearBlackListFilters() {
      this.blackListFilters = { category: null, type: null, status: null };
      this.showBlackListFilters = false;
      this.currentPage = 1;
      const payload = this.searchField ? { value: this.searchField } : undefined;
      this.getAllBlackList(payload);
    },
    async onSearch(term) {
      if (term) {
        this.currentPage = 1;
        this.searchField = term;
        await this.getAllBlackList({ value: term, ...this.blackListFilters });
      }
    },
    async onClear() {
      this.currentPage = 1;
      this.searchField = "";
      this.blackListFilters = { category: null, type: null, status: null };
      await this.getAllBlackList();
    },
    async paginate(page) {
      this.currentPage = page;
      const payload = { ...this.blackListFilters };
      if (this.searchField) payload.value = this.searchField;
      await this.getAllBlackList(Object.values(payload).some(Boolean) ? payload : undefined);
      window.scrollTo(0, 0);
    },
    formatDate(date) {
      if (!date) return "-";
      return new Date(date).toLocaleDateString("en-US", {
        year: "numeric", month: "short", day: "2-digit",
      });
    },
    async deleteRow(rowData) {
      const msg = "Are you sure you want to remove BlackList from the system?";
      const warningResponse = await deleteMessage(msg, "You won't be able to revert this!");
      if (warningResponse.isConfirmed) {
        const response = await this.$store.dispatch("blackList/deleteBlackListItem", rowData.id);
        if (response.status === 200 || response.status === 204) {
          successMessage("BlackList item has been deleted");
          if (this.data.length === 1 && this.currentPage > 1) this.currentPage -= 1;
          await this.getAllBlackList();
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@include table-action-icons;

.blacklist-filter {
  position: relative;
  margin-left: 1.5rem;
  &__backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 99;
  }
  &__btn {
    background-color: #00FF00 !important;
    border: 1px solid #00FF00 !important;
    color: #000000 !important;
    border-radius: 6px !important;
    width: 2.2rem;
    height: 2.2rem;
    padding: 0 !important;
    &--active {
      background-color: #00FF00 !important;
      border-color: #00FF00 !important;
      color: #000000 !important;
    }
    &:hover {
      background-color: #00e600 !important;
      border-color: #00e600 !important;
    }
  }
  &__dropdown {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    z-index: 100;
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    padding: 1rem;
    min-width: 240px;
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
  }
  &__field {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }
  &__label {
    font-size: 0.8rem;
    font-weight: 600;
    color: #495057;
    margin-bottom: 0;
  }
  &__select {
    width: 100%;
    :deep(.p-dropdown) {
      width: 100%;
    }
  }
  &__filter-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    margin-top: 0.4rem;
    border-top: 1px solid #dee2e6;
    padding-top: 0.8rem;
  }
  &__apply-btn {
    color: #000000 !important;
    background-color: #00FF00 !important;
    border: none !important;
    border-radius: 6px !important;
  }
}
</style>
